import Vue from 'vue';
import store from '@/store';

/**
 * The notification class. This class handles the manipulation of
 * notification configuration and sends notification.
 * 
 * @class Notification
 * @example
 * let foo = new Notification(); // init a notification configuration object with default values
 * foo.notify(); // sends the notification
 */
export class Notification {
  /**
   * Name of the notification holder.
   */
  group: NotificationGroup = NotificationGroup.Default;
  /**
   * The type of the notification. Class that will be assigned to the notification (warn, error, success, etc).
   */
  type: NotificationType = NotificationType.Error;
  /**
   * The title of the notification
   */
  title: string = NotificationType.Error;
  /**
   * The text of the notification. Content (will be wrapped in div.notification-content).
   */
  text: string = NotificationType.Error;
  /**
   * Time (ms) animation stays visible (if negative - notification will stay forever or until clicked)
   */
  duration: number = 20000;

  /**
   * Create a notification object with default configuration
   */
  constructor() {
  }

  /**
   * Send the notification
   */
  public notify() {
    Vue.notify({
      group: this.group,
      type: this.type,
      title: `errorTitles.${this.title}`,
      text: `errorMgs.${this.text}`,
      duration: this.duration,
    });
  }

  /**
   * Prepare the notification configuration based on the received errorMessage.
   * This prepares the notification for error where we didn't receive any response from the API.
   * 
   * @param errorMessage The error message to show on the notification
   * @param title The title to show on the notification (optional)
   */
  public prepare(errorMessage: string|undefined, title?: string): void {
    this.title = title || this.title;

    switch (errorMessage) {
      case 'networkError':
        this.text = errorMessage || this.text;
        this.group = NotificationGroup.Important;
        this.duration = -1;
        break;

      case 'sessionExpiration':
        this.type = NotificationType.Warning;
        this.text = errorMessage || this.text;
        this.group = NotificationGroup.Auth;
        break;

      default:
        this.text = 'unknownError';
        this.group = NotificationGroup.Important;
        break;
    }
  }

  /**
   * Prepare the notification configuration based on the received statusCode and errorMessage.
   * This prepares the notification for error where we received a response from the API.
   * 
   * @param statusCode The HTTP status code
   * @param errorMessage The error message to show on the notification
   * @param details The details of the error (optional)
   */
  public prepareWithStatus(statusCode: number, errorMessage: string|undefined, details?: object): void {

    switch (statusCode) {
      case 500:
        this.text = errorMessage || this.text;
        this.group = NotificationGroup.Important;
        this.duration = -1;
        break;

      case 400:
        this.type = NotificationType.Warning
        this.text = errorMessage || this.text;
        this.group = NotificationGroup.Default;
        if (!errorMessage) {
          this.title = 'questionnaireValidationFailed';
          this.text = 'pleaseCheckData';
        }
        if (this.text == 'questionnaireValidationFailed') {
          store.commit('setQuestionnaireValidationErrors', details);
        }
        break;

      case 403:
      case 404:
        this.type = NotificationType.Error
        this.text = errorMessage || this.text;
        this.group = NotificationGroup.Important;
        break;
        
      case 401:
        if (errorMessage == 'sessionExpiration') {
          this.prepare(errorMessage, 'warning');
        } else {
          this.type = NotificationType.Error;
          this.text = errorMessage || this.text;
          this.group = NotificationGroup.Auth;
        }
        break;

      default:
        this.text = 'error';
        this.group = NotificationGroup.Default;
        break;
    }
  }
}

export enum NotificationType {
  Success = 'success',
  Warning = 'warn',
  Error = 'error',
}

export enum NotificationGroup {
  Default = 'default',
  Auth = 'auth',
  Important = 'important'
}
